import styled from 'styled-components'
import AnimateHeight from 'react-animate-height'
import React, { useContext, useEffect, useState, Suspense, useCallback, createContext } from 'react'
import SpinnerOverlay from './SpinnerOverlay'
import { StepperContext } from '../../utils/Context/StepperContext'
import { CSSTransition } from 'react-transition-group'
import { ErrorPopup } from './ErrorPopup'

const MainWrapper = styled(AnimateHeight)`
  position: relative;
  margin: auto;
  max-width: 574px;
  min-height: 472px;
  box-sizing: content-box;
  padding: 60px;
  background: #f3efea;
  border-top-left-radius: 50px;
  border-top-right-radius: 50px;
  border: solid 1px #dad0ca;
  text-align: center;
  overflow: hidden;
  z-index: 1;
  font-family: var(--font-family);
  color: var(--text-color);

  @media (max-width: 680px) {
    padding: 20px 17px;
    padding-bottom: 60px;
  }

  @media (max-width: 400px) {
    padding: 20px 10px;
    padding-bottom: 30px;
  }
`

const MainFrameContext = createContext({
  autoResize: (scrollIntoView?: boolean, addedHeight?: number) => {
  },
  triggerErrorPopup: () => {
  }
})

const MainFrame = (props: { children: React.ReactNode }) => {
  
  const [animHeight, setAnimHeight] = useState<number | string>('auto')
  const [errorPopup, setErrorPopup] = useState(false)
  
  const { step } = useContext(StepperContext)
  
  const autoResize = useCallback((scrollIntoView?: boolean, addedHeight?: number) => {
    const wrapper = document.getElementById('housescan-main-wrapper')
    if (wrapper) {
      const wrapperDiv = wrapper.children[0]
      const calculatedHeight = wrapperDiv.clientHeight + (addedHeight || 0)
      setAnimHeight(calculatedHeight || 'auto')
      if (scrollIntoView) {
        const bodyRect = document.body.getBoundingClientRect().top
        const elementRect = wrapper.getBoundingClientRect().top
        const elementPosition = elementRect - bodyRect
        const offsetPosition = elementPosition - 70
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
      }
    }
  }, [])
  
  const triggerErrorPopup = useCallback(() => setErrorPopup(true), [])
  
  useEffect(() => {
    autoResize(step !== 1)
  }, [step, autoResize])
  
  useEffect(() => {
    errorPopup && setTimeout(() => setErrorPopup(false), 3000)
  }, [errorPopup])
  
  return (
    <MainWrapper id='housescan-main-wrapper' height={animHeight} duration={500}>
      <CSSTransition
        in={errorPopup}
        timeout={3000}
        classNames='fade'
        mountOnEnter={true}
        unmountOnExit={true}>
        <ErrorPopup />
      </CSSTransition>
      {errorPopup && <ErrorPopup />}
      <Suspense fallback={<SpinnerOverlay />}>
        <MainFrameContext.Provider value={{ autoResize, triggerErrorPopup }}>
          {props.children}
        </MainFrameContext.Provider>
      </Suspense>
    </MainWrapper>
  )
}

export { MainFrame, MainFrameContext }
